import type React from 'react'
import { useMemo } from 'react'
import { Empty, Typography } from 'antd'
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis
} from 'recharts'

const { Text } = Typography

const COST_PER_GB = 2.49

const TYPE_COLORS: Record<string, string> = {
  image: '#13c2c2',
  script: '#fa8c16',
  fetch: '#722ed1',
  xhr: '#eb2f96',
  document: '#52c41a',
  stylesheet: '#1677ff',
  font: '#a0d911',
  media: '#f5222d',
  websocket: '#fadb14',
  other: '#8c8c8c',
  manifest: '#bfbfbf',
  ping: '#d9d9d9',
  texttrack: '#9254de',
  preflight: '#85a5ff',
  signedexchange: '#73d13d',
  cspviolationreport: '#ff7875',
  eventsource: '#36cfc9'
}

const FALLBACK_COLORS = ['#9254de', '#36cfc9', '#ffc53d', '#ff85c0', '#73d13d', '#5cdbd3', '#ffa940']

const colorFor = (type: string, idx: number): string =>
  TYPE_COLORS[type] ?? FALLBACK_COLORS[idx % FALLBACK_COLORS.length]

const formatBytes = (b: number): string => {
  if (!b || b <= 0) return '0 B'
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(2) + ' GB'
  if (b >= 1024 ** 2) return (b / 1024 ** 2).toFixed(2) + ' MB'
  if (b >= 1024) return (b / 1024).toFixed(1) + ' KB'
  return `${Math.round(b)} B`
}

const pct = (part: number, whole: number): string =>
  whole > 0 ? ((part / whole) * 100).toFixed(0) + '%' : '0%'

const isObj = (v: any): boolean => typeof v === 'object' && v !== null
const mergeRecursive = (a: any, b: any): any => {
  const out: any = { ...(a ?? {}) }
  for (const k in (b ?? {})) {
    const av = a?.[k]
    const bv = b[k]
    if (isObj(bv) || isObj(av)) {
      out[k] = mergeRecursive(isObj(av) ? av : {}, isObj(bv) ? bv : {})
    } else {
      out[k] = (Number(av ?? 0)) + (Number(bv ?? 0))
    }
  }
  return out
}

const aggregateInRange = (data: Record<string, any> | undefined, hours: number): any => {
  if (!data) return {}
  const cutoff = Date.now() - hours * 60 * 60 * 1000
  let merged: any = {}
  for (const slot in data) {
    if (Number(slot) > cutoff) merged = mergeRecursive(merged, data[slot])
  }
  return merged
}

interface Props {
  data: Record<string, any> | undefined
  hours: number
  showCost?: boolean
}

export const NetworkConsumptionChart: React.FC<Props> = ({ data, hours, showCost = true }) => {
  const { totalBytes, sentBytes, receivedBytes, pieData, barData, types } = useMemo(() => {
    const merged = aggregateInRange(data, hours)
    const total = merged.total ?? {}
    const sent = merged.sent ?? {}
    const received = merged.received ?? {}

    const allTypes = new Set<string>([
      ...Object.keys(total),
      ...Object.keys(sent),
      ...Object.keys(received)
    ])
    allTypes.delete('all')
    const sortedTypes = Array.from(allTypes).sort(
      (a, b) => (total[b] ?? 0) - (total[a] ?? 0)
    )

    const pieData = sortedTypes
      .filter(t => (total[t] ?? 0) > 0)
      .map((t, i) => ({ name: t, value: total[t] ?? 0, color: colorFor(t, i) }))

    const buildRow = (label: string, src: any): any => {
      const row: any = { name: label }
      for (const t of sortedTypes) row[t] = src[t] ?? 0
      return row
    }
    const barData = [buildRow('Sent', sent), buildRow('Received', received)]

    return {
      totalBytes: Number(total.all ?? 0),
      sentBytes: Number(sent.all ?? 0),
      receivedBytes: Number(received.all ?? 0),
      pieData,
      barData,
      types: sortedTypes
    }
  }, [data, hours])

  if (!totalBytes) {
    return (
      <div style={{ width: 620, padding: 20 }}>
        <Empty description='Nessun dato nel periodo selezionato' image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    )
  }

  return (
    <div style={{ width: 680 }}>
      <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16, gap: 12 }}>
        <Stat
          label='Totale'
          value={formatBytes(totalBytes)}
          sub={showCost ? `$${(totalBytes / 1024 ** 3 * COST_PER_GB).toFixed(2)}` : undefined}
        />
        <Stat label='Inviati' value={formatBytes(sentBytes)} sub={pct(sentBytes, totalBytes)} />
        <Stat label='Ricevuti' value={formatBytes(receivedBytes)} sub={pct(receivedBytes, totalBytes)} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: 240, height: 240, flexShrink: 0 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={pieData}
                dataKey='value'
                nameKey='name'
                innerRadius={62}
                outerRadius={104}
                paddingAngle={1}
                isAnimationActive={false}
              >
                {pieData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
              </Pie>
              <RTooltip
                formatter={(v: any, name: any) => [`${formatBytes(Number(v) || 0)} (${pct(Number(v) || 0, totalBytes)})`, String(name)]}
                contentStyle={{ fontSize: 13, background: '#fff', color: 'rgba(0,0,0,0.88)', border: '1px solid #d9d9d9', borderRadius: 6 }}
                itemStyle={{ color: 'rgba(0,0,0,0.88)' }}
                labelStyle={{ color: 'rgba(0,0,0,0.88)' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{ flex: 1, minWidth: 0, paddingLeft: 12 }}>
          <Text type='secondary' style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Breakdown ({pieData.length})
          </Text>
          <div style={{ marginTop: 8 }}>
            {pieData.map(p => (
              <div key={p.name} style={{ display: 'flex', alignItems: 'center', fontSize: 13, marginBottom: 6 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    background: p.color,
                    marginRight: 8,
                    borderRadius: 2,
                    flexShrink: 0
                  }}
                />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </span>
                <span style={{ marginLeft: 8, opacity: 0.55, width: 38, textAlign: 'right' }}>
                  {pct(p.value, totalBytes)}
                </span>
                <span style={{ marginLeft: 10, fontVariantNumeric: 'tabular-nums', width: 80, textAlign: 'right' }}>
                  {formatBytes(p.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Text type='secondary' style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Sent vs Received
        </Text>
        <div style={{ height: 110, marginTop: 6 }}>
          <ResponsiveContainer>
            <BarChart data={barData} layout='vertical' margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <XAxis
                type='number'
                tickFormatter={(v: any) => formatBytes(Number(v) || 0)}
                fontSize={11}
                stroke='#bfbfbf'
              />
              <YAxis type='category' dataKey='name' fontSize={12} width={80} stroke='#8c8c8c' />
              <RTooltip
                formatter={(v: any, name: any) => [formatBytes(Number(v) || 0), String(name)]}
                contentStyle={{ fontSize: 13, background: '#fff', color: 'rgba(0,0,0,0.88)', border: '1px solid #d9d9d9', borderRadius: 6 }}
                itemStyle={{ color: 'rgba(0,0,0,0.88)' }}
                labelStyle={{ color: 'rgba(0,0,0,0.88)' }}
              />
              {types.map((t, i) => (
                <Bar key={t} dataKey={t} stackId='a' fill={colorFor(t, i)} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

const Stat: React.FC<{ label: string, value: string, sub?: string }> = ({ label, value, sub }) => (
  <div style={{ textAlign: 'center', flex: 1 }}>
    <div style={{ fontSize: 11, opacity: 0.55, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 19, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub != null && <div style={{ fontSize: 12, opacity: 0.6 }}>{sub}</div>}
  </div>
)
